# F88 — Asset Instance editor (edit) screen
# Route: /asset/instances/:id
# PUT /api/asset/instances/{id} (UpdateAssetInstanceRequest: Name?,
# AssetClassRef?, Location? — partial update, null = unchanged). The form
# always submits Name + AssetClassRef + Location; SerialNumber is immutable
# post-create and is not part of the update contract, so it renders read-only.

page-config-asset-instance-editor:
  type: Page
  archetype: edit
  meta:
    title: $t.config_asset_instance.editor_title
    route: /asset/instances/:id
    layout: shell
    auth: required
  params:
    id:
      type: string
      required: true
  data:
    instance:
      model: AssetInstance
      key: $route.id
      required: true
    classes:
      model: AssetClassOption[]
      refresh: on_mount
  spatial:
    display: flex
    direction: column
    gap: $spacing.lg
    padding: $spacing.lg
    padding_inline: $spacing.xl
    overflow: auto
  tokens:
    background: $color_scheme.light.color.surface
  children:
    - config-ai-editor-sm-notice
    - config-ai-editor-content

config-ai-editor-sm-notice:
  type: SmNotice
  content: $t.common.sm_notice_desktop

config-ai-editor-content:
  type: Section
  spatial:
    display: flex
    direction: column
    gap: $spacing.lg
    responsive:
      sm:
        display: none
  children:
    - config-ai-editor-header
    - config-ai-editor-form

config-ai-editor-header:
  type: PageHeader
  back_route: /asset/instances
  title_prefix: $t.config_asset_instance.title_prefix
  bind:
    content: instance.name

config-ai-editor-form:
  type: Form
  model: AssetInstance
  bind:
    content: $data.instance
  a11y:
    label: $t.config_asset_instance.editor_title
  action:
    type: update
    model: AssetInstance
    on_success: navigate(/asset/instances)
  spatial:
    display: flex
    direction: column
    gap: $spacing.md
  children:
    - config-ai-editor-field-name
    - config-ai-editor-field-class
    - config-ai-editor-serial-row
    - config-ai-editor-field-location
    - config-ai-editor-form-actions

config-ai-editor-field-name:
  type: Input
  input_type: text
  bind:
    value: name
  label: $t.config_asset_instance.field_name
  required: true
  a11y:
    label: $t.config_asset_instance.field_name

# options is $data-qualified: the Form's `bind: content: $data.instance`
# scopes unqualified child binds to the instance model, so the page-level
# `classes` alias must be addressed explicitly.
config-ai-editor-field-class:
  type: Input
  input_type: select
  bind:
    value: assetClassRef
    options: $data.classes
  label: $t.config_asset_instance.field_class
  required: true
  a11y:
    label: $t.config_asset_instance.field_class

# Serial number is immutable post-create (UpdateAssetInstanceRequest does not
# accept it) — shown read-only, same pattern as the role name on
# admin/role-edit.cir.yaml.
config-ai-editor-serial-row:
  type: Section
  spatial:
    display: flex
    direction: column
    gap: $spacing.xs
  children:
    - config-ai-editor-serial-label
    - config-ai-editor-serial-value

config-ai-editor-serial-label:
  type: Text
  content: $t.config_asset_instance.field_serial
  tokens:
    font_size: $font.size.sm
    color: $color_scheme.light.color.on-surface-muted

config-ai-editor-serial-value:
  type: Text
  bind:
    content: serialNumber
  tokens:
    font_size: $font.size.md
    font_family: $font.family.mono

# Placement (FR-5, DESIGN §D5) — M1 placement is ONE free-text field (maps to
# location.level1; deeper levels stay API/AI-only until the M2 entity-linked
# picker). Unqualified bind resolves against the Form's scoped instance model.
config-ai-editor-field-location:
  type: Input
  input_type: text
  bind:
    value: location.level1
  label: $t.config_asset_instance.field_location
  helper: $t.config_asset_instance.field_location_helper
  required: true
  a11y:
    label: $t.config_asset_instance.field_location

config-ai-editor-form-actions:
  type: FormActions
  submit_label: $t.common.btn_save
  cancel_route: /asset/instances
